/**
 * Readings tab for one data item: the readings list (normal operating range
 * line, rows of date and value with their source), the graph, the
 * prediction (component or live mode) and the correlation view. The item
 * comes from the Health tab's chart icon or the picker here; physical
 * components unlock prediction and correlation, live RFIMS items unlock the
 * live forecast.
 */
import { useMemo, useState } from 'react';
import { isRfims } from '@/features/rfims/registry';
import { rfimsHistoryExclusions } from '@/features/rfims/historyProvenance';
import { MeasuredGraph } from '@/features/rfims/MeasuredGraph';
import { RfimsHistorySummary } from '@/features/rfims/RfimsHistorySummary';
import cards from '@/features/rfims/cards.json';
import { useUI } from '@/state/store';
import { findSubsystem, useTwin } from '../../store';
import { predictComponent, predictLive } from '../adapters/analysis';
import { useInventoryView } from '../adapters/inventory';
import { SourceBadge } from '../components/badges';
import { EmptyState, SectionTitle, SubTabContent, SubTabs, inputClass } from '../components/layout';
import { useNowMs } from '../hooks/useNowMs';
import { useHasHistory, useReadingSeries } from '../hooks/useReadingSeries';
import { itemsWithHistory, pathLabel, pathTo, rootOf } from '../lib/cardTree';
import { fmtDateTime, parseDateMs } from '../lib/format';
import { extractUnit } from '../lib/values';
import { useTwinUi, type ReadingRef } from '../state';
import { CorrelationView } from './readings/CorrelationView';
import { PredictionCard } from './readings/PredictionCard';
import { ReadingGraph } from './readings/ReadingGraph';

export function ReadingsPanel() {
  const element = useTwin((s) => s.element);
  const selectedSubsystem = useUI((s) => s.selection.subsystemId);
  const reading = useTwinUi((s) => s.reading);
  const setReading = useTwinUi((s) => s.setReading);
  const hasHistory = useHasHistory();
  const [tab, setTab] = useState('readings');

  const options = useMemo(() => (element ? itemsWithHistory(element.subsystems, hasHistory) : []), [element, hasHistory]);
  const ref: ReadingRef | null = useMemo(() => {
    if (reading && options.some((o) => o.subsystemId === reading.subsystemId && o.dataId === reading.dataId)) return reading;
    const inSelected = selectedSubsystem ? options.find((o) => o.subsystemId === selectedSubsystem) : undefined;
    const first = inSelected ?? options[0];
    return first ? { subsystemId: first.subsystemId, dataId: first.dataId } : null;
  }, [reading, options, selectedSubsystem]);

  if (!element) return <EmptyState title="No twin loaded." className="m-3" />;
  if (options.length === 0) return <EmptyState title="No readings with history on this asset." className="m-3" />;

  const grouped = new Map<string, typeof options>();
  for (const o of options) {
    const list = grouped.get(o.subsystemLabel) ?? [];
    list.push(o);
    grouped.set(o.subsystemLabel, list);
  }
  const key = ref ? `${ref.subsystemId}.${ref.dataId}` : '';
  const root = ref ? rootOf(element.subsystems, ref.subsystemId) : undefined;
  const isPhysical = root?.id === 'physical_components';

  return (
    <div className="flex flex-col gap-3 p-3 text-xs">
      <label className="flex flex-col gap-1">
        <span className="text-[10px] uppercase tracking-wider text-ink-3">Reading</span>
        <select
          aria-label="Reading"
          value={key}
          onChange={(e) => {
            const [subsystemId, dataId] = e.target.value.split('.');
            setReading({ subsystemId, dataId });
          }}
          className={inputClass}
        >
          {[...grouped.entries()].map(([label, items]) => (
            <optgroup key={label} label={label}>
              {items.map((o) => (
                <option key={`${o.subsystemId}.${o.dataId}`} value={`${o.subsystemId}.${o.dataId}`}>
                  {o.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </label>
      {ref && <ReadingDetail ref_={ref} tab={tab} setTab={setTab} isPhysical={isPhysical} />}
    </div>
  );
}

function ReadingDetail({ ref_, tab, setTab, isPhysical }: { ref_: ReadingRef; tab: string; setTab: (t: string) => void; isPhysical: boolean }) {
  const element = useTwin((s) => s.element)!;
  const [days, setDays] = useState(7);
  const series = useReadingSeries(ref_, days);
  const inventory = useInventoryView();
  // A minute tick: the TODAY line and the prediction dates do not need finer updates.
  const now = useNowMs(60_000, isRfims(element.id));
  const subsystem = findSubsystem(element.subsystems, ref_.subsystemId);
  const item = series.item;
  const unit = (isRfims(element.id) ? cards.find(c => c.id === ref_.subsystemId)?.items.find(i => i[0] === ref_.dataId)?.[3] : undefined) ?? (item?.normalRange?.unit?.trim() || extractUnit(item?.value) || '');
  const spare = inventory.bySubsystem(ref_.subsystemId);
  const replacementsMs = useMemo(
    () => (subsystem?.events ?? []).filter((e) => e.type === 'replacement').map((e) => parseDateMs(e.date)).filter((t): t is number => t !== null),
    [subsystem],
  );
  const predictionEnabled = !isRfims(element.id) && (isPhysical || series.isLive);
  const correlationEnabled = isPhysical;
  const activeTab = (tab === 'prediction' && !predictionEnabled) || (tab === 'correlation' && !correlationEnabled) ? 'readings' : tab;

  const prediction = useMemo(() => {
    if (!predictionEnabled || series.isText) return null;
    if (series.isLive) return predictLive(series.points, ref_, now);
    return predictComponent(series.points, item?.normalRange, ref_.dataId, ref_.subsystemId, now);
  }, [predictionEnabled, series, ref_, item, now]);

  const path = subsystem ? pathLabel(pathTo(element.subsystems, subsystem.id)) : ref_.subsystemId;
  const rows = series.rows.slice().reverse();

  return (
    <div className="flex flex-col gap-2">
      {series.isLive && <label className="flex items-center justify-between gap-2 text-ink-2">Measured history · UTC<select aria-label="History range" className={inputClass} value={days} onChange={e => setDays(Number(e.target.value))}>{[1, 7, 30, 90].map(d => <option key={d} value={d}>{d} days</option>)}</select></label>}
      {series.isLive && <p className="text-[10px] text-ink-3">Historical records are aggregated into {days <= 2 ? '5-minute' : days <= 7 ? '30-minute' : days <= 31 ? '2-hour' : '6-hour'} windows. Timestamps mark window ends; new session measurements retain their sample times.</p>}
      {isRfims(element.id) && rfimsHistoryExclusions(days, now).length > 0 && <p role="note" className="rounded-control border border-warning/30 p-2 text-[11px] text-warning">History from 5 June through 23 August 2026 UTC is withheld: legacy generated records may be mixed with measurements. This interval is excluded before aggregation. Stored records are unchanged.</p>}
      <div>
        <div className="text-[10px] text-ink-3">{path}</div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-display text-sm font-semibold text-ink">{item?.label ?? ref_.dataId}</span>
          {series.sources.map((s) => (
            <SourceBadge key={s} source={s} pulse={s === 'live'} />
          ))}
          {series.liveStatus === 'loading' && <span className="text-[10px] text-ink-3">Loading live series</span>}
        </div>
        {series.liveStatus === 'error' && <div className="mt-1 text-[11px] text-warning">Measured history unavailable: {series.liveError}. Any remaining points are measured samples received during this session.</div>}
        {item?.description && <p className="mt-0.5 leading-snug text-ink-3">{item.description}</p>}
      </div>
      {isRfims(element.id)&&<RfimsHistorySummary points={series.points} days={days} subsystemId={ref_.subsystemId} dataId={ref_.dataId}/>}
      <SubTabs
        value={activeTab}
        onChange={setTab}
        tabs={[
          { key: 'readings', label: 'Readings' },
          { key: 'graph', label: 'Graph' },
          { key: 'prediction', label: 'Prediction', disabled: !predictionEnabled, title: predictionEnabled ? undefined : 'Prediction applies to physical components and live readings' },
          { key: 'correlation', label: 'Correlation', disabled: !correlationEnabled, title: correlationEnabled ? undefined : 'Correlation applies to physical components' },
        ]}
      >
        <SubTabContent value="readings">
          {item?.normalRange && (
            <div className="mb-2 rounded-control border border-nominal/30 bg-nominal/5 px-2 py-1 text-[11px] text-ink-2">
              Normal operating range: <span className="font-mono text-ink">{item.normalRange.low}{unit}</span> to <span className="font-mono text-ink">{item.normalRange.high}{unit}</span>
            </div>
          )}
          {rows.length === 0 ? (
            <EmptyState title="No historical data for this reading." />
          ) : (
            <ul className="max-h-[360px] divide-y divide-hairline overflow-y-auto rounded-control border border-hairline scrollbar-thin">
              {rows.map((r) => (
                <li key={r.t} className="flex items-center justify-between gap-2 px-2 py-1 font-mono text-[11px]">
                  <span className="text-ink-3">{fmtDateTime(r.t)}</span>
                  <span className="flex items-center gap-2 text-ink">
                    {r.raw}
                    <SourceBadge source={r.source} />
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-1 text-[10px] text-ink-3">{rows.length} readings, newest first.</div>
        </SubTabContent>
        <SubTabContent value="graph">
          {isRfims(element.id) ? <MeasuredGraph points={series.points} unit={unit} days={days} discrete={['door','relay','leak'].includes(ref_.dataId)}/> : <ReadingGraph points={series.points} normalRange={item?.normalRange} unit={unit} nowMs={now} replacementsMs={replacementsMs} inventory={spare} isText={series.isText} />}
        </SubTabContent>
        <SubTabContent value="prediction">
          <SectionTitle>{series.isLive ? 'Live threshold forecast' : 'Component wear model'}</SectionTitle>
          <PredictionCard prediction={prediction} inventory={spare} />
          {!series.isLive && <p className="mt-2 text-[10px] text-ink-3">Model: regression on the stored readings plus a sensor wear profile (SIM); confidence 50 to 95.</p>}
        </SubTabContent>
        <SubTabContent value="correlation">
          <CorrelationView dataId={ref_.dataId} subsystemId={ref_.subsystemId} />
        </SubTabContent>
      </SubTabs>
    </div>
  );
}
